// how-it-works.ts — ported from the design attachment's p6-standalone.js (5730-5832), the real,
// live animation for the #how-it-works ("p6") section.
//
// anim-p4.js (5833-6656) is the OTHER script the brief assigns to this section, but almost none
// of it applies here. Verified against the source: `hasLegacyHiw` (5881) requires
// `#how-it-works [data-anim="hiw-step"]` to have exactly 3 matches, and the shipped "p6" markup
// (3855-3955, ported verbatim in ../sections/how-it-works.tsx) has zero such elements — it uses
// `.p6-card` instead. Every #how-it-works-specific timeline anim-p4.js defines (`reveal-how-it-
// works`, `steps-connect`, the `.np4-step-card`/`.np4-progress`/`.np4-route-packet` markup it
// builds at 5922-6143) lives inside `if (hasLegacyHiw) { … }` and never runs against this markup.
// It is dead code, one script generation behind the p6 redesign that superseded it — nothing from
// that block is ported here.
//
// What anim-p4.js's init() DOES still do unconditionally, once every section it touches (stats,
// games, faq, cta-final, nav — none of them this task's job) has the shape it expects: add
// `np-p4` to the page root as its very last step (source 6650). Per the brief's cross-task
// contract, whichever task ports anim-p4.js owns that toggle, so this module adds it once its own
// section mounts. Verified safe to add now, ahead of those other sections' own future tasks: every
// `.trial-beta-page.np.np-p4` rule in trial-beta.css either (a) targets `[data-np4]` — an attribute
// only ever set by the dead-for-us `init()` tail this module doesn't port, so it matches nothing
// yet — or (b) targets a runtime-injected class (`.np4-step-card`, `.np-nav-bg`, `.np-cta-amb`,
// `.np4-stat-card`, …) that no current markup carries either, so it also matches nothing. Nothing
// currently visible changes; the toggle only starts mattering once those later tasks add the
// classes it gates.
//
// Copy split matches multi-internet.ts: the static no-JS resting state is page copy via `t(...)`
// in how-it-works.tsx; everything this module overwrites at runtime comes from
// `strings.experienceShowcase` and `strings.common` (see strings.ts's own note on why that
// section's real copy lives in `experienceShowcase` rather than the dead `howItWorks` group).
import type { SectionModule } from './use-section-animation';

const PAGE_ROOT_SELECTOR = '.trial-beta-page';
const ORCHESTRATION_CLASS = 'np-p4';
const CYCLE_MS = 7600;
// Source 5758: clicking "download" rewinds the demo to just after the button-click flash, so the
// visitor watches the install → select-game → connect story play out again almost from the start.
const REPLAY_ANCHOR_MS = 500;
const REPLAY_HOLD_MS = 2200;
// Source: the reduced-motion branch fixes `t` at 7100ms — deep enough into the cycle that the
// download is installed, the last game (Fortnite) is selected and the connection is optimized, so
// a visitor who never sees the loop still sees its settled, "finished" frame.
const REDUCED_MOTION_T_MS = 7100;

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

// Source's `smooth()` — a smoothstep ease shared by every timeline in this module.
function smooth(value: number): number {
  const clamped = clamp01(value);
  return clamped * clamped * (3 - 2 * clamped);
}

export const howItWorksAnimation: SectionModule = ({ root, strings, clock }) => {
  const copy = strings.experienceShowcase;
  const { games } = copy;

  const download = root.querySelector<HTMLElement>('.p6-download');
  const downloadButton = root.querySelector<HTMLButtonElement>('.p6-download__button');
  const downloadPercent = root.querySelector<HTMLElement>('[data-download-percent]');
  const downloadStatus = root.querySelector<HTMLElement>('[data-download-status]');
  const gameCovers = root.querySelectorAll<HTMLElement>('[data-game-cover]');
  const gameButtons = root.querySelectorAll<HTMLButtonElement>('[data-game-button]');
  const gameName = root.querySelector<HTMLElement>('[data-game-name]');
  const gameStatus = root.querySelector<HTMLElement>('[data-game-status]');
  const performancePanel = root.querySelector<HTMLElement>('.p6-performance');
  const connectionState = root.querySelector<HTMLElement>('[data-connection-state]');
  const pingValue = root.querySelector<HTMLElement>('[data-ping-value]');
  const fpsValue = root.querySelector<HTMLElement>('[data-fps-value]');
  const fpsGain = root.querySelector<HTMLElement>('[data-fps-gain]');
  const jitterValue = root.querySelector<HTMLElement>('[data-jitter-value]');
  const lossValue = root.querySelector<HTMLElement>('[data-loss-value]');
  const performanceStatus = root.querySelector<HTMLElement>('[data-performance-status]');
  const cards = root.querySelectorAll<HTMLElement>('.p6-card');
  const connectors = root.querySelectorAll<HTMLElement>('.p6-connector');
  const fpsBars = root.querySelectorAll<HTMLElement>('.p6-fps-bars i');

  if (!download || !downloadButton || !downloadPercent || !downloadStatus || !gameName || !gameStatus) return () => {};
  if (!performancePanel || !connectionState || !pingValue || !fpsValue || !fpsGain || !jitterValue || !lossValue || !performanceStatus) return () => {};
  if (gameCovers.length !== 4 || gameButtons.length !== 4 || cards.length !== 3 || connectors.length !== 2 || fpsBars.length !== 8) return () => {};

  root.closest<HTMLElement>(PAGE_ROOT_SELECTOR)?.classList.add(ORCHESTRATION_CLASS);

  let selectedGame = -1;
  let manualGameIndex: number | null = null;
  let manualUntil = 0;
  let cycleOffset = 0;
  let lastElapsed = 0;
  let started = false;

  function setGame(index: number): void {
    if (index === selectedGame) return;
    selectedGame = index;
    gameCovers.forEach((cover, coverIndex) => cover.classList.toggle('is-active', coverIndex === index));
    gameButtons.forEach((button, buttonIndex) => {
      button.classList.toggle('is-active', buttonIndex === index);
      button.setAttribute('aria-pressed', buttonIndex === index ? 'true' : 'false');
    });
    const game = games[index];
    if (game) gameName!.textContent = game.name;
  }

  function render(t: number, reduced: boolean): void {
    const cursorProgress = reduced ? 1 : smooth(t / 500);
    const clicking = !reduced && t >= 520 && t <= 820;
    const launch = reduced ? 0 : 1 - smooth((t - 800) / 160);
    const installer = reduced ? 1 : smooth((t - 970) / 180);
    const progress = reduced ? 1 : smooth((t - 1100) / 750);
    root.style.setProperty('--launch-opacity', launch.toFixed(3));
    root.style.setProperty('--launch-y', `${((1 - launch) * -7).toFixed(1)}px`);
    root.style.setProperty('--cursor-x', `${(82 - cursorProgress * 39).toFixed(1)}px`);
    root.style.setProperty('--cursor-y', `${(-68 + cursorProgress * 91).toFixed(1)}px`);
    root.style.setProperty('--cursor-opacity', (reduced ? 0 : 1 - smooth((t - 820) / 140)).toFixed(3));
    root.style.setProperty('--installer-opacity', installer.toFixed(3));
    root.style.setProperty('--installer-scale', (0.92 + installer * 0.08).toFixed(3));
    root.style.setProperty('--download-width', `${(progress * 100).toFixed(1)}%`);
    download!.classList.toggle('is-clicking', clicking);
    download!.classList.toggle('is-launch-hidden', launch < 0.015);
    downloadPercent!.textContent = `${Math.round(progress * 100)}%`;
    let downloadStatusText: string;
    if (t < 520 && !reduced) downloadStatusText = copy.readyToDownload;
    else if (progress <= 0.01) downloadStatusText = copy.downloadStarted;
    else if (progress < 0.995) downloadStatusText = strings.common.downloadingPercent(progress * 100);
    else downloadStatusText = strings.common.installReady;
    downloadStatus!.textContent = downloadStatusText;

    const autoGame = t < 2000 ? 0 : Math.max(0, Math.min(3, Math.floor((t - 2000) / 340)));
    if (manualGameIndex !== null && lastElapsed >= manualUntil) manualGameIndex = null;
    let activeGame: number;
    if (manualGameIndex !== null) activeGame = manualGameIndex;
    else if (reduced) activeGame = 3;
    else activeGame = autoGame;
    setGame(activeGame);
    const activeGameCopy = games[activeGame];
    if (activeGameCopy) gameStatus!.textContent = t < 2000 && !reduced ? copy.gameQueued(activeGameCopy.short) : copy.gameSelected(activeGameCopy.short);

    const offline = !reduced && t < 3700;
    const onlineWarm = !reduced && t >= 3700 && t < 4200;
    const boost = reduced ? 1 : smooth((t - 4200) / 1500);
    performancePanel!.classList.toggle('is-offline', offline);
    performancePanel!.classList.toggle('is-online', !offline);
    if (offline) {
      connectionState!.textContent = strings.common.offline;
      pingValue!.textContent = copy.offlinePlaceholder;
      fpsValue!.textContent = '84';
      fpsGain!.textContent = '+0%';
      jitterValue!.textContent = copy.offlinePlaceholder;
      lossValue!.textContent = copy.offlinePlaceholder;
      performanceStatus!.textContent = copy.pingOffline;
      root.style.setProperty('--ping-angle', '34deg');
      root.style.setProperty('--perf-glow', '.035');
    } else {
      const ping = Math.round(86 - 77 * boost);
      const fps = Math.round(84 + 80 * boost);
      const jitter = 18 - 16.8 * boost;
      const loss = 2.4 * (1 - boost);
      let connectionStateText: string;
      if (onlineWarm) connectionStateText = strings.common.online;
      else if (boost < 0.995) connectionStateText = strings.common.optimizing;
      else connectionStateText = strings.common.optimized;
      connectionState!.textContent = connectionStateText;
      pingValue!.textContent = String(ping);
      fpsValue!.textContent = String(fps);
      fpsGain!.textContent = `+${Math.round((fps / 84 - 1) * 100)}%`;
      jitterValue!.textContent = jitter.toFixed(1);
      lossValue!.textContent = loss < 0.05 ? '0.0' : loss.toFixed(1);
      root.style.setProperty('--ping-angle', `${(88 + boost * 257).toFixed(1)}deg`);
      root.style.setProperty('--perf-glow', (0.055 + boost * 0.18).toFixed(3));
      let performanceStatusText: string;
      if (onlineWarm) performanceStatusText = copy.pingOnline;
      else if (boost < 0.995) performanceStatusText = copy.loweringPingBoostingFps;
      else performanceStatusText = copy.pingLockedFps(ping, fps);
      performanceStatus!.textContent = performanceStatusText;
    }

    fpsBars.forEach((bar, index) => {
      // Assigning through the callback parameter's `.style` trips no-param-reassign; the alias is
      // the same escape hatch multi-internet.ts's own `setText()` uses for its event-bar nodes.
      const barElement = bar;
      const base = 0.18 + ((index * 5) % 4) * 0.07;
      const target = 0.48 + index * 0.07;
      barElement.style.transform = `scaleY(${(base + (target - base) * boost).toFixed(3)})`;
      barElement.classList.toggle('is-live', boost > index / 11);
    });

    let activeStep: number;
    if (t < 2000) activeStep = 0;
    else if (t < 3450) activeStep = 1;
    else activeStep = 2;
    cards.forEach((card, index) => {
      card.classList.toggle('is-active', index === activeStep);
      card.classList.toggle('is-complete', index < activeStep);
    });
    const firstConnector = connectors[0];
    const secondConnector = connectors[1];
    firstConnector?.classList.toggle('is-flowing', !reduced && t >= 1840 && t < 2270);
    secondConnector?.classList.toggle('is-flowing', !reduced && t >= 3290 && t < 3720);
  }

  const listeners = new AbortController();
  const { signal } = listeners;

  gameButtons.forEach((button, index) => {
    button.addEventListener(
      'click',
      () => {
        manualGameIndex = index;
        manualUntil = lastElapsed + REPLAY_HOLD_MS;
        setGame(index);
        const game = games[index];
        if (game) gameStatus.textContent = copy.gameSelected(game.short);
      },
      { signal }
    );
  });

  // Under reduced motion no further frames ever arrive (createClock() never starts its loop), so
  // this rewinds `cycleOffset` but nothing re-renders from it — the button stays inert, which is
  // the correct behaviour for a visitor who asked for less motion, not a special case to code for.
  downloadButton.addEventListener(
    'click',
    () => {
      cycleOffset = lastElapsed - REPLAY_ANCHOR_MS;
    },
    { signal }
  );

  function step(elapsedMs: number): void {
    if (!Number.isFinite(elapsedMs)) {
      render(REDUCED_MOTION_T_MS, true);
      return;
    }
    if (!started) {
      started = true;
      root.classList.add('is-running');
    }
    lastElapsed = elapsedMs;
    const local = elapsedMs - cycleOffset;
    const t = ((local % CYCLE_MS) + CYCLE_MS) % CYCLE_MS;
    render(t, false);
  }

  // Paint the resting frame before the first tick, matching multi-internet.ts's own convention,
  // so the demo is never shown one frame behind its own static markup.
  render(0, false);
  setGame(0);

  const unsubscribe = clock.subscribe(step);

  return () => {
    unsubscribe();
    listeners.abort();
    root.classList.remove('is-running');
  };
};
